import { Search, User, Inbox, MessageSquarePlus } from "lucide-react";
import { Input } from "../../tailwind/input";
import { Badge } from "../../tailwind/badge";
import { cn } from "../../../lib/utils";
import { showCreateConversation } from "../../../utils/featureFlags";
import { waSoft } from "../../../pages/whatsapp/utils/whatsappUiTokens";

function ConversationListItem({ chat, isSelected, onSelect }) {
  const hasName = Boolean(chat.customerName);
  const displayName = chat.name || `${chat.customerId?.slice(0, 8) || "Unknown"}`;
  const displayMessage = chat.lastMessage || "";
  const avatarInitial = hasName ? displayName.trim().charAt(0).toUpperCase() : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(chat.id)}
      className={cn(
        "w-full text-left px-3 py-3 transition-colors flex items-start gap-3 border-b border-gray-100/80 dark:border-slate-800/80",
        "hover:bg-slate-50 dark:hover:bg-slate-800/50",
        isSelected &&
          "bg-[hsl(158,48%,36%)]/8 dark:bg-[hsl(158,48%,36%)]/10 hover:bg-[hsl(158,48%,36%)]/12 dark:hover:bg-[hsl(158,48%,36%)]/15 border-l-[3px] border-l-[hsl(158,48%,36%)] pl-[9px]"
      )}
    >
      <div
        className={cn(
          "h-12 w-12 rounded-full flex items-center justify-center font-medium shrink-0",
          isSelected
            ? "bg-[hsl(158,48%,36%)]/20 text-[hsl(158,48%,32%)] dark:text-[hsl(158,45%,58%)]"
            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
        )}
      >
        {avatarInitial ? (
          <span className="text-sm font-semibold">{avatarInitial}</span>
        ) : (
          <User className="h-5 w-5" />
        )}
      </div>

      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex justify-between items-start gap-2 mb-0.5">
          <h4 className="font-semibold text-sm text-gray-900 dark:text-slate-100 truncate">
            {displayName}
          </h4>
          <span
            className={cn(
              "text-xs whitespace-nowrap shrink-0 mt-0.5",
              chat.unread > 0
                ? "text-[hsl(158,48%,32%)] dark:text-[hsl(158,45%,55%)] font-semibold"
                : "text-gray-400 dark:text-slate-500"
            )}
          >
            {chat.time || ""}
          </span>
        </div>

        <div className="flex justify-between items-center gap-2">
          {displayMessage && (
            <p
              className={cn(
                "text-sm truncate",
                chat.unread > 0
                  ? "text-gray-700 dark:text-slate-200 font-medium"
                  : "text-gray-500 dark:text-slate-400"
              )}
            >
              {displayMessage}
            </p>
          )}
          {chat.unread > 0 && (
            <Badge className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5 border-none shadow-none text-xs font-bold">
              {chat.unread}
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}

export default function ConversationList({
  conversations,
  searchQuery,
  onSearchQueryChange,
  selectedChatId,
  onSelectChat,
  isHiddenOnMobile,
  activeTab,
  onTabChange,
  tabCounts,
  onNewConversationClick,
}) {
  return (
    <div
      className={cn(
        "h-full min-h-0 w-full md:w-[350px] xl:w-[380px] 2xl:w-[420px] shrink-0 flex flex-col",
        "border-r border-gray-200 dark:border-slate-800",
        "bg-slate-50/80 dark:bg-slate-950",
        isHiddenOnMobile ? "hidden md:flex" : "flex"
      )}
    >
      <div className="p-3 space-y-3 border-b border-gray-200/80 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500" />
            <Input
              placeholder="Search name or mobile number"
              className="pl-9 h-10 rounded-xl text-sm bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-gray-900 dark:text-slate-100 focus-visible:ring-[hsl(158,48%,36%)]"
              value={searchQuery}
              onChange={(event) => onSearchQueryChange(event.target.value)}
            />
          </div>
          {showCreateConversation && (
            <button
              type="button"
              className="h-10 w-10 bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white rounded-xl flex items-center justify-center shrink-0 transition-colors shadow-sm"
              onClick={onNewConversationClick}
              title="New Conversation"
            >
              <MessageSquarePlus className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800/80 gap-1">
          {["ACTIVE", "REQUESTING"].map((tab) => {
            const isActive = activeTab === tab;
            const count = tabCounts?.[tab] || 0;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => onTabChange(tab)}
                className={cn(
                  "flex-1 py-2.5 px-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-all",
                  isActive
                    ? "bg-white dark:bg-slate-900 text-[hsl(158,48%,32%)] dark:text-[hsl(158,45%,55%)] shadow-sm ring-1 ring-black/5 dark:ring-white/10"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                )}
              >
                <span className="inline-flex items-center justify-center gap-1.5">
                  {tab === "ACTIVE" ? "Active" : "Requesting"}
                  <span
                    className={cn(
                      "inline-flex items-center justify-center min-w-[1.35rem] h-5 px-1.5 rounded-full text-xs font-bold tabular-nums",
                      isActive ? waSoft.chip : "bg-slate-200/80 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                    )}
                  >
                    {count}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-900">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
            <div className="h-14 w-14 rounded-2xl bg-[hsl(158,48%,36%)]/10 dark:bg-[hsl(158,48%,36%)]/15 flex items-center justify-center">
              <Inbox size={28} className="text-[hsl(158,48%,36%)] dark:text-[hsl(158,45%,55%)]" />
            </div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 text-center">
              No {activeTab === "ACTIVE" ? "active" : "requesting"} conversations
            </p>
          </div>
        ) : (
          conversations.map((chat) => (
            <ConversationListItem
              key={chat.id}
              chat={chat}
              isSelected={selectedChatId === chat.id}
              onSelect={onSelectChat}
            />
          ))
        )}
      </div>
    </div>
  );
}
